import {matchPath, useLocation} from 'react-router-dom';

import {has} from 'ramda';
import {isNotNil, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {
  accountingRoutes,
  businessCaseRoutes,
  customerRoutes,
  employeeRoutes,
  importRoutes,
  interestRoutes,
  interestsRoutes,
  opportunitiesKanbanRoutes,
  rentalOrderRoutes,
  settingsRoutes,
  tiresInventoryRoutes,
  vehiclesRoutes,
  warehouseRoutes,
  workshopRoutes,
} from '@dms/routes';

import {useQueryState} from 'shared/web';

import {queryParams} from '../utils/queryParams';

type SingleSourceNavigationLink = {
  nested: string | string[];
  exclude?: string[];
  navigateTo: string;
  title: string;
  rowControlDetail?: string;
  sources?: never;
};

type MultiSourceNavigationLink = {
  nested: string | string[];
  exclude?: string[];
  navigateTo?: never;
  title?: never;
  sources: Record<
    string,
    {
      navigateTo: string;
      title: string;
      rowControlRoute?: string;
      rowControlDetail?: string;
    }
  >;
};
type NavigationLink = SingleSourceNavigationLink | MultiSourceNavigationLink;
type NavigationLinksType = Record<string, NavigationLink>;

/**
 * @example
 * [key: EntityId]{
 *      nested: string // witch part od route must be present in
 *      navigateTo: string // path that breadcrumb should navigate to after click
 *      title: string // text of the breadcrumb
 * }
 * @example
 * [key: EntityId]{
 *      nested: string
 *      sources: { // used when more than one place redirects to the same route
 *          [key: SourceId]: { // value of the query param 'source'
 *             navigateTo: string
 *             title: string
 *          }
 * }
 */
const NAVIGATION_LINKS: NavigationLinksType = {
  customers: {
    navigateTo: customerRoutes.overview,
    title: `${i18n.t('general.labels.backToCustomer')}`,
    rowControlDetail: customerRoutes.rowControlDetail,
    exclude: [customerRoutes.gdprConsentsOverview],
    nested: [
      customerRoutes.GDPRConsents,
      customerRoutes.activityLogs,
      customerRoutes.businessCases,
      customerRoutes.create,
      customerRoutes.customerContracts,
      customerRoutes.customerInformation,
      customerRoutes.documents,
      customerRoutes.interests,
      customerRoutes.serviceCases,
      customerRoutes.tires,
      customerRoutes.detail,
      customerRoutes.vehicles,
      customerRoutes.emailActivityLogs,
      customerRoutes.invoices,
      customerRoutes.checklists,
      customerRoutes.tasks,
    ],
  },
  suppliers: {
    nested: customerRoutes.detail,
    navigateTo: settingsRoutes.suppliers,
    exclude: [customerRoutes.gdprConsentsOverview],
    title: `${i18n.t('general.labels.backToSuppliers')}`,
  },
  interests: {
    nested: interestsRoutes.tabDetail,
    navigateTo: interestsRoutes.home,
    title: `${i18n.t('general.labels.backToInterest')}`,
    rowControlDetail: interestsRoutes.tabDetail,
  },
  rentalOrders: {
    nested: [
      rentalOrderRoutes.create,
      rentalOrderRoutes.vehicleAndCustomer,
      rentalOrderRoutes.rentVehicle,
      rentalOrderRoutes.returnVehicle,
      rentalOrderRoutes.rentVehicleInspections,
      rentalOrderRoutes.returnVehicleInspections,
      rentalOrderRoutes.documents,
      rentalOrderRoutes.activityLogs,
      rentalOrderRoutes.emailActivityLogs,
      rentalOrderRoutes.photos,
      rentalOrderRoutes.serviceOrder,
    ],
    sources: {
      'rental-order-detail': {
        navigateTo: rentalOrderRoutes.overview,
        title: `${i18n.t('general.labels.backToRentalOrder')}`,
        rowControlDetail: rentalOrderRoutes.rowControlDetail,
      },
      'rental-order-kanban': {
        navigateTo: rentalOrderRoutes.overviewKanban,
        title: `${i18n.t('general.labels.backToRentalOrderKanban')}`,
        rowControlDetail: rentalOrderRoutes.rowControlDetail,
      },
      'rental-order-calendar': {
        navigateTo: rentalOrderRoutes.overviewCalendar,
        title: `${i18n.t('general.labels.backToRentalOrderCalendar')}`,
        rowControlDetail: rentalOrderRoutes.rowControlDetail,
      },
    },
  },
  interest: {
    nested: interestRoutes.detail,
    sources: {
      interest: {
        navigateTo: interestRoutes.overview,
        title: `${i18n.t('general.labels.backToInterest')}`,
        rowControlDetail: interestRoutes.detail,
      },
      'opportunities-kanban': {
        navigateTo: opportunitiesKanbanRoutes.home,
        title: `${i18n.t('general.labels.backToOpportunitiesKanban')}`,
      },
    },
  },
  businessCase: {
    nested: [
      businessCaseRoutes.concept,
      businessCaseRoutes.overview,
      businessCaseRoutes.customer,
      businessCaseRoutes.buying,
      businessCaseRoutes.selling,
      businessCaseRoutes.testDrive,
      businessCaseRoutes.reservations,
      businessCaseRoutes.emailActivityLogs,
      businessCaseRoutes.insurance,
      businessCaseRoutes.checkout,
      businessCaseRoutes.complaints,
      businessCaseRoutes.documents,
      businessCaseRoutes.serviceCases,
      businessCaseRoutes.activityLog,
      businessCaseRoutes.tasks,
      businessCaseRoutes.checklists,
    ],
    sources: {
      'business-case': {
        navigateTo: businessCaseRoutes.home,
        title: `${i18n.t('general.labels.backToBusinessCase')}`,
        rowControlDetail: businessCaseRoutes.detail,
      },
      'business-case-complaints-list': {
        navigateTo: businessCaseRoutes.complaintsList,
        title: `${i18n.t('general.labels.backToBusinessCaseComplaintsList')}`,
      },
      'opportunities-kanban': {
        navigateTo: opportunitiesKanbanRoutes.home,
        title: `${i18n.t('general.labels.backToOpportunitiesKanban')}`,
      },
    },
  },
  imports: {
    nested: importRoutes.detail,
    navigateTo: importRoutes.overview,
    title: `${i18n.t('general.labels.backToImports')}`,
  },
  vehicles: {
    nested: [
      vehiclesRoutes.detail,
      vehiclesRoutes.conditions,
      vehiclesRoutes.conditionDetail,
      vehiclesRoutes.conditionDetailTab,
      vehiclesRoutes.inspectionDetail,
      vehiclesRoutes.inspectionDetailTab,
      vehiclesRoutes.conditionsInspections,
      vehiclesRoutes.vehicleWarehouseInfo,
      vehiclesRoutes.emailActivityLogs,
      vehiclesRoutes.finances,
      vehiclesRoutes.financesPrices,
      vehiclesRoutes.financesEarningsCosts,
      vehiclesRoutes.financesRentalPrices,
      vehiclesRoutes.publishing,
      vehiclesRoutes.photos,
      vehiclesRoutes.photoByInspectionId,
      vehiclesRoutes.threeHundredAndSixtyPhotos,
      vehiclesRoutes.documents,
      vehiclesRoutes.customers,
      vehiclesRoutes.interests,
      vehiclesRoutes.testDrives,
      vehiclesRoutes.reservations,
      vehiclesRoutes.businessCases,
      vehiclesRoutes.complaints,
      vehiclesRoutes.activityLog,
      vehiclesRoutes.insights,
      vehiclesRoutes.vehicleData,
      vehiclesRoutes.rental,
      vehiclesRoutes.features,
      vehiclesRoutes.serviceIntervals,
      vehiclesRoutes.additionalInformation,
      vehiclesRoutes.checklists,
      vehiclesRoutes.tasks,
      vehiclesRoutes.postponedJobs,
      vehiclesRoutes.ownership,
      vehiclesRoutes.historyOfRepairs,
      vehiclesRoutes.serviceCampaigns,
      vehiclesRoutes.tires,
    ],
    navigateTo: vehiclesRoutes.home,
    title: `${i18n.t('general.labels.backToVehicle')}`,
    rowControlDetail: vehiclesRoutes.rowControlDetail,
  },
  create_service_case: {
    nested: workshopRoutes.draftServiceCase,
    sources: {
      'workshop-cases': {
        navigateTo: workshopRoutes.serviceCases,
        title: `${i18n.t('general.labels.backToWorkshopCases')}`,
        rowControlDetail: workshopRoutes.draftServiceCase,
      },
      'business-case': {
        navigateTo: businessCaseRoutes.home,
        title: `${i18n.t('general.labels.backToBusinessCase')}`,
      },
    },
  },
  workshop_cases: {
    nested: workshopRoutes.serviceCaseDetail,
    sources: {
      'service-orders': {
        navigateTo: workshopRoutes.serviceOrders,
        title: `${i18n.t('general.labels.backToWorkshopOrders')}`,
        rowControlDetail: workshopRoutes.serviceCaseDetail,
      },
      'service-cases': {
        navigateTo: workshopRoutes.serviceCases,
        title: `${i18n.t('general.labels.backToWorkshopCases')}`,
        rowControlDetail: workshopRoutes.serviceCaseDetail,
      },
      'business-case': {
        navigateTo: businessCaseRoutes.home,
        title: `${i18n.t('general.labels.backToBusinessCase')}`,
      },
    },
  },
  serviceOrderIssues: {
    nested: [
      warehouseRoutes.serviceOrderIssueDetailOverview,
      warehouseRoutes.serviceOrderIssueDetailIssueNotes,
      warehouseRoutes.serviceOrderIssueDetailDocuments,
      warehouseRoutes.serviceOrderIssueDetailChecklists,
    ],
    navigateTo: warehouseRoutes.serviceOrderIssues,
    title: i18n.t('general.labels.backToServiceOrderIssues'),
    rowControlDetail: warehouseRoutes.serviceOrderIssueDetailRowControl,
  },
  issueNotes: {
    nested: [
      warehouseRoutes.issueNoteDetailOverview,
      warehouseRoutes.issueNoteDetailDocuments,
      warehouseRoutes.issueNoteDetailIssueNoteCorrections,
      warehouseRoutes.issueNoteDetailCostCorrections,
    ],
    navigateTo: warehouseRoutes.issueNotes,
    title: i18n.t('general.labels.backToIssueNotes'),
    rowControlDetail: warehouseRoutes.issueNoteDetailRowControl,
  },
  issueNoteCorrections: {
    nested: [
      warehouseRoutes.issueNoteCorrectionDetailOverview,
      warehouseRoutes.issueNoteCorrectionDetailDocuments,
    ],
    navigateTo: warehouseRoutes.issueNoteCorrections,
    title: i18n.t('general.labels.backToIssueNoteCorrections'),
    rowControlDetail: warehouseRoutes.issueNoteCorrectionDetailRowControl,
  },
  serviceOrderReturns: {
    nested: [
      warehouseRoutes.serviceOrderReturnsDetailOverview,
      warehouseRoutes.serviceOrderReturnsDetailDocuments,
      warehouseRoutes.serviceOrderReturnsDetailChecklists,
    ],
    navigateTo: warehouseRoutes.serviceOrderReturns,
    title: i18n.t('general.labels.backToServiceOrderReturns'),
    rowControlDetail: warehouseRoutes.serviceOrderReturnsDetailRowControl,
  },
  fastTrackIssues: {
    nested: [
      warehouseRoutes.fastTrackIssuesDetailOverview,
      warehouseRoutes.fastTrackIssuesDetailIssueNotes,
      warehouseRoutes.fastTrackIssuesDetailDocuments,
    ],
    navigateTo: warehouseRoutes.fastTrackIssues,
    title: i18n.t('general.labels.backToFastTrackIssues'),
    rowControlDetail: warehouseRoutes.fastTrackIssuesDetailRowControl,
  },
  costCorrections: {
    nested: [
      warehouseRoutes.costCorrectionDetailOverview,
      warehouseRoutes.costCorrectionDetailDocuments,
    ],
    navigateTo: warehouseRoutes.costCorrections,
    title: i18n.t('general.labels.backToCostCorrections'),
    rowControlDetail: warehouseRoutes.costCorrectionDetailRowControl,
  },
  deliveryNotes: {
    nested: warehouseRoutes.deliveryNoteDetailOverview,
    navigateTo: warehouseRoutes.deliveryNoteList,
    title: i18n.t('general.labels.backToDeliveryNotes'),
    rowControlDetail: warehouseRoutes.deliveryNoteDetailRowControl,
  },
  warehouseReceiveNotes: {
    nested: [
      warehouseRoutes.receiveNoteDetailOverview,
      warehouseRoutes.receiveNoteDetailDocuments,
      warehouseRoutes.receiveNoteDetailChecklists,
    ],
    navigateTo: warehouseRoutes.receiveNoteList,
    title: i18n.t('general.labels.backToReceiveNotes'),
    rowControlDetail: warehouseRoutes.receiveNoteDetailRowControl,
  },
  warehouseReceiveNoteCorrections: {
    nested: [
      warehouseRoutes.receiveNoteCorrectionDetailOverview,
      warehouseRoutes.receiveNoteCorrectionDetailDocuments,
      warehouseRoutes.receiveNoteCorrectionDetailChecklists,
    ],
    navigateTo: warehouseRoutes.receiveNoteCorrectionsList,
    title: i18n.t('general.labels.backToReceiveNoteCorrections'),
    rowControlDetail: warehouseRoutes.receiveNoteCorrectionDetailRowControl,
  },
  warehousesDirectSales: {
    nested: [
      warehouseRoutes.directSalesDetailOverview,
      warehouseRoutes.directSalesDetailDocuments,
      warehouseRoutes.directSalesDetailVehicle,
      warehouseRoutes.directSalesDetailCustomer,
      warehouseRoutes.directSalesDetailChecklists,
    ],
    sources: {
      'warehouse-direct-sales': {
        navigateTo: warehouseRoutes.directSalesList,
        title: i18n.t('general.labels.backToDirectSales'),
        rowControlDetail: warehouseRoutes.directSalesDetailRowControl,
      },
      'workshop-direct-sales': {
        navigateTo: workshopRoutes.directSales,
        title: i18n.t('general.labels.backToDirectSales'),
        rowControlDetail: warehouseRoutes.directSalesDetailRowControl,
      },
    },
  },
  warehousesDetail: {
    nested: [
      warehouseRoutes.articleDetailOverview,
      warehouseRoutes.articleDetailAvailability,
      warehouseRoutes.articleDetailReceipts,
      warehouseRoutes.articleDetailMovements,
      warehouseRoutes.articleDetailReservations,
      warehouseRoutes.articleDetailSupplierArticles,
      warehouseRoutes.articleDetailDocuments,
      warehouseRoutes.articleDetailChecklists,
    ],
    navigateTo: warehouseRoutes.articleList,
    title: `${i18n.t('general.labels.backToWarehouseArticles')}`,
    rowControlDetail: warehouseRoutes.articleDetailRowControl,
  },
  supplierOrders: {
    nested: [
      warehouseRoutes.supplierOrderDetailOverview,
      warehouseRoutes.supplierOrderDetailDocuments,
    ],
    navigateTo: warehouseRoutes.supplierOrderList,
    title: i18n.t('general.labels.backToSupplierOrders'),
    rowControlDetail: warehouseRoutes.supplierOrderDetailRowControl,
  },
  updateJobs: {
    nested: [
      warehouseRoutes.updateJobCreate,
      warehouseRoutes.updateJobDetailOverview,
      warehouseRoutes.updateJobDetailUpdateList,
      warehouseRoutes.updateJobDetailUpdateResults,
    ],
    navigateTo: warehouseRoutes.updateJobsList,
    title: i18n.t('general.labels.backToUpdateJobs'),
    rowControlDetail: warehouseRoutes.updateJobDetailRowControl,
  },
  transfers: {
    nested: [
      warehouseRoutes.transferOrderDraft,
      warehouseRoutes.transferOrderDetailOverview,
      warehouseRoutes.transferOrderDetailIssueNotes,
      warehouseRoutes.transferOrderDetailDocuments,
    ],
    navigateTo: warehouseRoutes.transfers,
    title: i18n.t('general.labels.backToTransfers'),
    rowControlDetail: warehouseRoutes.transferOrderDetailRowControl,
  },
  accounting_invoice: {
    nested: [
      accountingRoutes.invoiceDetail,
      accountingRoutes.invoiceDraft,
      accountingRoutes.invoiceNew,
      accountingRoutes.newCorrectiveTaxDocument,
      accountingRoutes.correctiveTaxDocumentDetail,
      accountingRoutes.balanceInvoiceDetail,
      accountingRoutes.proformaInvoiceDetail,
      accountingRoutes.taxDocumentForPaymentDetail,
    ],
    navigateTo: accountingRoutes.invoiceOverview,
    title: `${i18n.t('general.labels.backToAccountingInvoices')}`,
  },
  accounting_cashReceipt: {
    nested: [
      accountingRoutes.cashReceiptDetail,
      accountingRoutes.createCashReceiptExpense,
      accountingRoutes.createCashReceiptIncome,
    ],
    navigateTo: accountingRoutes.cashReceiptsOverview,
    title: `${i18n.t('general.labels.backToAccountingReceipts')}`,
  },
  employees: {
    nested: [
      employeeRoutes.detail,
      employeeRoutes.organizationalPlacement,
      employeeRoutes.userDataAndRole,
      employeeRoutes.personalInfo,
      employeeRoutes.externalApiKeys,
      employeeRoutes.externalSystems,
      employeeRoutes.activityLogs,
      employeeRoutes.externalAuthorizationSystems,
    ],
    navigateTo: employeeRoutes.overview,
    title: `${i18n.t('general.labels.backToEmployees')}`,
    rowControlDetail: employeeRoutes.employeeDetailRowControl,
  },
  tireInventory: {
    nested: tiresInventoryRoutes.tireOrderDetail,
    sources: {
      'tire-orders': {
        navigateTo: tiresInventoryRoutes.tireOrders,
        title: `${i18n.t('general.labels.backToTireOrders')}`,
      },
      'tire-list': {
        navigateTo: tiresInventoryRoutes.tireList,
        title: `${i18n.t('general.labels.backToListOfTires')}`,
      },
    },
  },
  serviceCampaign: {
    nested: workshopRoutes.serviceCampaignDetail,
    navigateTo: workshopRoutes.serviceCampaigns,
    title: `${i18n.t('general.labels.backToServiceCampaigns')}`,
    rowControlDetail: workshopRoutes.serviceCampaignDetail,
  },
  selectableMaterials: {
    nested: [settingsRoutes.workshopSelectableMaterialsDetail],
    navigateTo: settingsRoutes.workshopSelectableMaterials,
    title: `${i18n.t('general.labels.backToSelectableMaterials')}`,
  },
};

export const useNavigationBreadCrumb = () => {
  const location = useLocation();
  const [redirectSource] = useQueryState(queryParams.REDIRECT_SOURCE);
  const [returnUrl] = useQueryState(queryParams.RETURN_URL);

  const navigationLink = Object.values(NAVIGATION_LINKS).find((breadcrumbProps) => {
    const isExcluded = breadcrumbProps?.exclude?.some((excludePath) =>
      matchPath({path: excludePath}, location.pathname)
    );

    if (isExcluded) {
      return false;
    }

    const path = breadcrumbProps?.nested;

    if (isString(path)) {
      return matchPath({path}, location.pathname);
    }

    return path.some((subPath) => matchPath({path: subPath}, location.pathname));
  });

  let breadCrumbProps =
    isNotNil(navigationLink) && isMultiSource(navigationLink)
      ? navigationLink.sources[redirectSource ?? ''] || Object.values(navigationLink.sources)[0]
      : navigationLink;

  if (isNotNilOrEmpty(returnUrl) && isNotNil(breadCrumbProps)) {
    breadCrumbProps = {...breadCrumbProps, navigateTo: returnUrl ?? breadCrumbProps.navigateTo};
  }

  const shouldDisplayBreadCrumb = isNotNilOrEmpty(breadCrumbProps);

  return [breadCrumbProps, shouldDisplayBreadCrumb] as const;
};

const isMultiSource = (obj: NavigationLink): obj is MultiSourceNavigationLink =>
  has('sources', obj);
